/**
 * Squish fold rule: butterfly blot printing on a voxel sheet of paper.
 *
 * The paper is a layer of solid cells with its top at `paperTop`; ink is stacks of `ink` cells
 * sitting above it. Folding along a crease presses every ink column onto its mirror partner:
 *
 * 1. Columns. An ink column at (u, w) is the bottom-up stack of ink cells above the paper, where u
 *    is the fold axis coordinate (x for axis 'x', z for axis 'z') and w the other one. Its partner
 *    is u' = M - u with M = 2 * hinge - 1 (the crease runs between columns hinge - 1 and hinge; a
 *    half-integer hinge puts the crease through the middle of column hinge - 0.5, which then pairs
 *    with itself). Columns whose partner falls outside the bounds are not touched at all.
 * 2. Press. Both partners get a merged stack of n = clamp(ceil((t + t') / 2), 1, MAX_PRESSED_LAYERS)
 *    cells (raised to the number of distinct colours while that fits, PRESS_KEEPS_EVERY_COLOUR;
 *    also clamped to the room under bounds.y). Which colours survive and how many cells each
 *    gets is decided from the union colour counts of the pair (so both sides agree): every distinct
 *    colour keeps one cell while they fit (most abundant first, ties by colour value), extra cells
 *    go to the most abundant colours (D'Hondt). Each colour is one contiguous run; with
 *    PRESS_ORDER 'own-first' a side's own colours sit below the partner's (the physical order: what
 *    was on this side touches this paper), with 'mirror' both sides use the same order.
 *    Multiply blending is order independent, so both orders render identically.
 * 3. Spread. Every pressed pair whose combined thickness t + t' >= SPREAD_MIN_THICKNESS squeezes
 *    single-cell ink into empty columns along walks in the 8 directions: radius
 *    min(SPREAD_MAX_RADIUS, floor((t + t') / 2)), each step taken with SPREAD_STEP_CHANCE[k]
 *    (diagonals scaled by SPREAD_DIAGONAL_FACTOR), a walk stops at the first failed step, and
 *    SPREAD_TENDRIL_CHANCE of walks reach the full SPREAD_MAX_RADIUS. A spread cell takes a colour
 *    from the source stack (abundance weighted); where walks of different colours meet, the target
 *    gets up to SPREAD_MAX_LAYERS stacked colours (so fringes mix too). Spread is computed on the
 *    low half (u <= M / 2) only and mirrored, so the result is exactly symmetric. Its noise is a hash
 *    of (column, direction, step, foldIndex, axis) that is also symmetric about the centre of the
 *    other axis, so a fold along x then z with centred creases keeps 4-fold symmetry.
 *
 * Pure: reads ink cells above the paper, never writes into the paper, below it, outside the bounds,
 * above bounds.y - 1, or over any non-ink cell.
 */
import { parseKeyXYZ, posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'

export const TINT_TYPE = 'tint'
/** Colour used for ink cells without one (should not happen in practice). */
export const TINT_FALLBACK_COLOR = 0x202020

/** Maximum cells in a pressed column. */
export const MAX_PRESSED_LAYERS = 3
/**
 * Lets a pressed column grow beyond ceil((t + t') / 2) (up to MAX_PRESSED_LAYERS) so every distinct
 * colour keeps a cell: a yellow drop pressed onto a cyan drop reads green on both sides.
 */
export const PRESS_KEEPS_EVERY_COLOUR = true
/** Combined thickness (both sides, before pressing) from which a pair squeezes ink outward. */
export const SPREAD_MIN_THICKNESS = 2
/** Hard cap on how many columns a walk can push ink. */
export const SPREAD_MAX_RADIUS = 3
/** Chance of each successive walk step (the last value repeats). */
export const SPREAD_STEP_CHANCE: readonly number[] = [0.62, 0.4, 0.26]
/** Diagonal steps are rarer, which keeps edges lobed instead of square. */
export const SPREAD_DIAGONAL_FACTOR = 0.45
/** Chance a walk ignores the thickness radius and may reach SPREAD_MAX_RADIUS (tendrils / veins). */
export const SPREAD_TENDRIL_CHANCE = 0.08
/** Maximum stacked colours in a spread (fringe) column. */
export const SPREAD_MAX_LAYERS = 2

export type FoldAxis = 'x' | 'z'
export type FoldParams = { axis: FoldAxis; hinge: number; paperTop: number; foldIndex: number }
export type FoldResult = { set: Array<[number, VoxelData]>; clear: number[] }
export type PressOrder = 'own-first' | 'mirror'
export const PRESS_ORDER: PressOrder = 'own-first'

export function isFoldParams(p: unknown): p is FoldParams {
  if (!p || typeof p !== 'object') return false
  const o = p as Record<string, unknown>
  return (o.axis === 'x' || o.axis === 'z')
    && typeof o.hinge === 'number' && Number.isInteger(o.hinge * 2)
    && typeof o.paperTop === 'number' && Number.isInteger(o.paperTop)
    && typeof o.foldIndex === 'number' && Number.isInteger(o.foldIndex)
}

/** Centred crease for a side of `size` cells (half-integer for odd sizes: crease through the middle column). */
export function centreHinge(size: number): number {
  return size / 2
}

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
]

function mix32(h: number): number {
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d)
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b)
  return (h ^ (h >>> 16)) >>> 0
}

function seedOf(u: number, wc: number, du: number, dw: number, foldIndex: number, axisId: number): number {
  let h = mix32((foldIndex | 0) ^ 0x9e3779b9)
  h = mix32(h ^ Math.imul(u + 1, 0x27d4eb2f))
  h = mix32(h ^ Math.imul(wc + 1, 0x165667b1))
  h = mix32(h ^ (((du + 1) * 3 + (dw + 1)) * 0x45d9f3b) ^ (axisId << 7))
  return h
}

function rand(seed: number, n: number): number {
  return mix32(seed ^ Math.imul(n + 1, 0x85ebca6b)) / 4294967296
}

type Cell = { y: number; color: number }

/** Union colour counts of a pair, reduced to `n` cells (see file comment, step 2). */
function planCounts(a: readonly number[], b: readonly number[], n: number): Map<number, number> {
  const counts = new Map<number, number>()
  for (const c of a) counts.set(c, (counts.get(c) ?? 0) + 1)
  for (const c of b) counts.set(c, (counts.get(c) ?? 0) + 1)
  const colours = [...counts.keys()].sort((x, y) => (counts.get(y)! - counts.get(x)!) || x - y)
  const out = new Map<number, number>()
  if (colours.length >= n) {
    for (let i = 0; i < n; i++) out.set(colours[i], 1)
    return out
  }
  for (const c of colours) out.set(c, 1)
  for (let extra = n - colours.length; extra > 0; extra--) {
    let best = colours[0], bestScore = -1
    for (const c of colours) {
      const score = counts.get(c)! / (out.get(c)! + 1)
      if (score > bestScore) { best = c; bestScore = score }
    }
    out.set(best, out.get(best)! + 1)
  }
  return out
}

/** Lays out planned counts bottom-up, colours ordered by first appearance in `first` then `second`. */
function layout(plan: Map<number, number>, first: readonly number[], second: readonly number[]): number[] {
  const out: number[] = []
  const seen = new Set<number>()
  const visit = (c: number) => {
    if (seen.has(c)) return
    seen.add(c)
    const k = plan.get(c)
    if (k) for (let i = 0; i < k; i++) out.push(c)
  }
  for (const c of first) visit(c)
  for (const c of second) visit(c)
  return out
}

type Pressed = { low: number[]; high: number[]; thickness: number }

const EMPTY: FoldResult = { set: [], clear: [] }

export function foldInk(
  base: VoxelMap,
  bounds: Readonly<Position>,
  params: FoldParams,
  order: PressOrder = PRESS_ORDER,
): FoldResult {
  if (!isFoldParams(params)) return EMPTY
  const { axis, hinge, paperTop, foldIndex } = params
  const M = Math.round(hinge * 2) - 1
  const isX = axis === 'x'
  const bu = isX ? bounds.x : bounds.z
  const bw = isX ? bounds.z : bounds.x
  const yLo = Math.max(0, paperTop + 1)
  const room = bounds.y - yLo
  if (room <= 0 || bu <= 0 || bw <= 0) return EMPTY
  const uLo = Math.max(0, M - (bu - 1))
  const uMid = Math.floor(M / 2)
  if (uMid < uLo) return EMPTY

  const keyOf = isX
    ? (u: number, y: number, w: number) => posKeyXYZ(u, y, w)
    : (u: number, y: number, w: number) => posKeyXYZ(w, y, u)

  // 1. Read ink columns that have a partner (column id = u * bw + w).
  const columns = new Map<number, Cell[]>()
  const oldKeys: number[] = []
  ;(base as { forEach(fn: (v: VoxelData, k: number) => void): void }).forEach((v, key) => {
    if (v.type !== TINT_TYPE) return
    const [x, y, z] = parseKeyXYZ(key)
    if (y < yLo || y >= bounds.y) return
    const u = isX ? x : z
    const w = isX ? z : x
    if (u < 0 || u >= bu || w < 0 || w >= bw) return
    const p = M - u
    if (p < 0 || p >= bu) return
    oldKeys.push(key)
    const ci = u * bw + w
    let col = columns.get(ci)
    if (!col) columns.set(ci, col = [])
    col.push({ y, color: typeof v.color === 'number' ? v.color : TINT_FALLBACK_COLOR })
  })
  if (columns.size === 0) return EMPTY
  const coloursOf = (ci: number): number[] => {
    const col = columns.get(ci)
    if (!col) return []
    col.sort((a, b) => a.y - b.y)
    return col.map(c => c.color)
  }

  // 2. Press, keyed by the low-half column id.
  const pressed = new Map<number, Pressed>()
  for (const ci of columns.keys()) {
    const u = Math.floor(ci / bw), w = ci - u * bw
    const lowU = Math.min(u, M - u)
    const lowCi = lowU * bw + w
    if (pressed.has(lowCi)) continue
    const highCi = (M - lowU) * bw + w
    const lowOwn = coloursOf(lowCi)
    const highOwn = lowCi === highCi ? lowOwn : coloursOf(highCi)
    const thickness = lowOwn.length + highOwn.length
    const distinct = PRESS_KEEPS_EVERY_COLOUR ? new Set([...lowOwn, ...highOwn]).size : 1
    const n = Math.min(MAX_PRESSED_LAYERS, room, Math.max(1, Math.ceil(thickness / 2), distinct))
    const plan = planCounts(lowOwn, highOwn, n)
    const low = layout(plan, lowOwn, highOwn)
    const high = order === 'own-first' ? layout(plan, highOwn, lowOwn) : low
    pressed.set(lowCi, { low, high, thickness })
  }

  const blockedAt = (u: number, y: number, w: number): boolean => {
    const v = base.get(keyOf(u, y, w))
    return v !== undefined && v.type !== TINT_TYPE
  }

  // 3. Spread on the low half only (mirrored on write).
  const fringe = new Map<number, Set<number>>()
  const sortedIds = [...pressed.keys()].sort((a, b) => a - b)
  for (const ci of sortedIds) {
    const src = pressed.get(ci)!
    if (src.thickness < SPREAD_MIN_THICKNESS) continue
    const u = Math.floor(ci / bw), w = ci - u * bw
    const radius = Math.min(SPREAD_MAX_RADIUS, Math.floor(src.thickness / 2))
    const palette = [...src.low].sort((a, b) => a - b)
    const wMirror = bw - 1 - w
    const wc = Math.min(w, wMirror)
    for (const [du, dw] of DIRS) {
      const dwc = w === wMirror ? Math.abs(dw) : (w > wMirror ? -dw : dw)
      const seed = seedOf(u, wc, du, dwc, foldIndex, isX ? 1 : 2)
      const reach = rand(seed, 0) < SPREAD_TENDRIL_CHANCE ? SPREAD_MAX_RADIUS : radius
      const scale = du !== 0 && dw !== 0 ? SPREAD_DIAGONAL_FACTOR : 1
      let uu = u, ww = w
      for (let k = 0; k < reach; k++) {
        const chance = SPREAD_STEP_CHANCE[Math.min(k, SPREAD_STEP_CHANCE.length - 1)] * scale
        if (rand(seed, k + 1) >= chance) break
        uu += du; ww += dw
        if (uu < uLo || uu > uMid || ww < 0 || ww >= bw) break
        const tci = uu * bw + ww
        if (pressed.has(tci)) continue
        if (blockedAt(uu, yLo, ww) || blockedAt(M - uu, yLo, ww)) break
        const colour = palette[Math.floor(rand(seed, 64 + k) * palette.length)]
        let set = fringe.get(tci)
        if (!set) fringe.set(tci, set = new Set())
        set.add(colour)
      }
    }
  }

  // 4. Assemble the result.
  const finalCells = new Map<number, number>()
  const writeStack = (u: number, w: number, stack: readonly number[]) => {
    const n = Math.min(stack.length, room)
    for (let i = 0; i < n; i++) {
      const y = yLo + i
      if (blockedAt(u, y, w)) continue
      finalCells.set(keyOf(u, y, w), stack[i])
    }
  }
  for (const [ci, p] of pressed) {
    const u = Math.floor(ci / bw), w = ci - u * bw
    writeStack(u, w, p.low)
    if (M - u !== u) writeStack(M - u, w, p.high)
  }
  for (const [ci, colours] of fringe) {
    const u = Math.floor(ci / bw), w = ci - u * bw
    const stack = [...colours].sort((a, b) => a - b).slice(0, SPREAD_MAX_LAYERS)
    writeStack(u, w, stack)
    if (M - u !== u) writeStack(M - u, w, stack)
  }

  const tintData = new Map<number, VoxelData>()
  const set: Array<[number, VoxelData]> = []
  for (const [key, color] of finalCells) {
    const old = base.get(key)
    if (old && old.type === TINT_TYPE && old.color === color) continue
    let v = tintData.get(color)
    if (!v) tintData.set(color, v = { type: TINT_TYPE, color })
    set.push([key, v])
  }
  const clear: number[] = []
  for (const key of oldKeys) if (!finalCells.has(key)) clear.push(key)
  return { set, clear }
}

/** Applies a fold result in place (base must be mutable). */
export function applyFoldResult(base: VoxelMap, result: FoldResult): void {
  for (const key of result.clear) base.delete(key)
  for (const [key, v] of result.set) base.set(key, v)
}
